import { useAtomValue, useSetAtom } from 'jotai'
import React from 'react'

import { auth0UserAtom, surveyFilterAtom } from '~/atoms'
import { type QuestionFilter, type SegmentFilter } from '~/gql/graphql'
import { useCurrentSurvey } from '~/hooks/useCurrentSurvey'
import { exists, safeGetLocalStorageItem, toSurveyFilterV2 } from '~/utils'

/**
 * 古い形式の surveyFilter を localStorage から取得して、新しい形式に変換して surveyFilterAtom にセットする関数を返すカスタムフックです。
 *
 * @returns surveyFilterAtom にセットする関数。
 */
export const useSetSurveyFilterFromLocalStorageV1 = () => {
  const setSurveyFilter = useSetAtom(surveyFilterAtom)
  const survey = useCurrentSurvey()
  const user = useAtomValue(auth0UserAtom)

  const popSurveyFilterV1Item = React.useCallback(
    <T>(
      suffix: 'count' | 'questionFilters' | 'segmentFilters',
    ): T | undefined => {
      const surveyId = survey.success ? survey.value.id : undefined
      const key = ['analyzer', surveyId, user?.sub, suffix].join('::')
      const value = safeGetLocalStorageItem<T>(key)

      localStorage.removeItem(key)

      return value
    },
    [survey, user?.sub],
  )

  return React.useCallback(
    (questions: Parameters<typeof toSurveyFilterV2>[1]) => {
      const count = popSurveyFilterV1Item<number>('count')
      const questionFilters =
        popSurveyFilterV1Item<QuestionFilter[]>('questionFilters')
      const segmentFilters =
        popSurveyFilterV1Item<SegmentFilter[]>('segmentFilters')

      if (
        !exists(count) ||
        !exists(questionFilters) ||
        !exists(segmentFilters)
      ) {
        return
      }

      const surveyFilter = {
        count,
        filterGroups: toSurveyFilterV2(
          { questionFilters, segmentFilters },
          questions,
        ),
      }
      setSurveyFilter(surveyFilter)
    },
    [popSurveyFilterV1Item, setSurveyFilter],
  )
}
